import { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import { Instagram, ExternalLink } from "lucide-react";
import { Button } from "./ui/button";

const INSTAGRAM_URL = "https://www.instagram.com/shoresightmarine";
const WIDGET_CLASS_REGEX = /elfsight-app-[a-f0-9-]+/;
const PLATFORM_SCRIPT_SRC = "https://elfsightcdn.com/platform.js";

/** Extract the Elfsight widget class from raw embed HTML stored in the DB. */
function extractWidgetClass(embed: string): string | null {
  const match = embed.match(WIDGET_CLASS_REGEX);
  return match ? match[0] : null;
}

/**
 * Try to (re)initialize Elfsight. Different versions of platform.js expose
 * different APIs, so we attempt every known entry point defensively.
 */
function tryReinitElfsight(): boolean {
  const w = window as unknown as {
    elfsight?: { reinit?: () => void; initialize?: () => void };
    eapps?: { initialize?: () => void; reinit?: () => void };
  };
  try {
    if (typeof w.elfsight?.reinit === "function") {
      w.elfsight.reinit();
      return true;
    }
    if (typeof w.elfsight?.initialize === "function") {
      w.elfsight.initialize();
      return true;
    }
    if (typeof w.eapps?.initialize === "function") {
      w.eapps.initialize();
      return true;
    }
    if (typeof w.eapps?.reinit === "function") {
      w.eapps.reinit();
      return true;
    }
  } catch {
    /* swallow — we'll retry on the next tick */
  }
  return false;
}

/**
 * Inject the Elfsight platform script (idempotent). Returns the script
 * element so callers can wait for `load`.
 */
function ensurePlatformScript(): HTMLScriptElement {
  const existing = document.querySelector<HTMLScriptElement>(
    `script[src="${PLATFORM_SCRIPT_SRC}"]`
  );
  if (existing) return existing;
  const script = document.createElement("script");
  script.src = PLATFORM_SCRIPT_SRC;
  script.async = true;
  script.defer = true;
  document.body.appendChild(script);
  return script;
}

export const InstagramFeed = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const widgetRef = useRef<HTMLDivElement>(null);
  const [isNearViewport, setIsNearViewport] = useState(false);
  const location = useLocation();

  // Defer data fetching until section is near the viewport
  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsNearViewport(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { data: settings, isLoading } = useQuery({
    queryKey: ["settings-instagram"],
    enabled: isNearViewport,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("settings")
        .select("elfsight_instagram_embed")
        .eq("id", 1)
        .maybeSingle();

      if (error) {
        console.warn("InstagramFeed: failed to load settings", error.message);
        return null;
      }
      return data;
    },
    retry: false,
  });

  const widgetClass = useMemo(
    () =>
      settings?.elfsight_instagram_embed
        ? extractWidgetClass(settings.elfsight_instagram_embed)
        : null,
    [settings?.elfsight_instagram_embed]
  );

  /**
   * Robust (re)initialization:
   *  - Inject platform.js if missing.
   *  - Wait for `load` if the script is still loading.
   *  - Retry reinit a handful of times in case the global isn't ready yet.
   *  - Re-runs whenever the route or widget class changes so SPA navigation
   *    back to a page hosting the widget never leaves it blank.
   */
  const initWidget = useCallback(() => {
    if (!widgetClass || !widgetRef.current) return () => {};

    const script = ensurePlatformScript();
    let attempts = 0;
    const maxAttempts = 12; // ~6s with 500ms cadence
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cancelled = false;

    const attempt = () => {
      if (cancelled) return;
      const ok = tryReinitElfsight();
      // Even if reinit succeeded, the widget may still mount after platform.js
      // observes the DOM. Give it a moment, then verify the widget rendered.
      const widgetHasContent =
        (widgetRef.current?.childElementCount ?? 0) > 0;

      if (ok && widgetHasContent) return;
      if (attempts++ >= maxAttempts) return;
      timer = setTimeout(attempt, 500);
    };

    // If script not yet loaded, wait for it before first attempt.
    const onLoad = () => attempt();
    if (script.dataset.loaded === "true") {
      attempt();
    } else {
      script.addEventListener("load", onLoad, { once: true });
      script.addEventListener(
        "load",
        () => {
          script.dataset.loaded = "true";
        },
        { once: true }
      );
      // Fallback: if the script was added by a previous mount and already
      // finished loading before we attached the listener, kick off attempts
      // anyway after a short delay.
      timer = setTimeout(attempt, 300);
    }

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
      script.removeEventListener("load", onLoad);
    };
  }, [widgetClass]);

  // Re-run on widget mount AND on route changes (SPA navigation back).
  useEffect(() => {
    const cleanup = initWidget();
    return cleanup;
  }, [initWidget, location.pathname]);

  // Loading / pre-viewport skeleton
  if (isLoading || !isNearViewport) {
    return (
      <section ref={sectionRef} className="py-16 md:py-20 bg-accent/30">
        <div className="container mx-auto px-4">
          <div className="flex justify-center">
            <div className="animate-pulse bg-muted h-64 w-full max-w-4xl rounded-lg" />
          </div>
        </div>
      </section>
    );
  }

  // No embed configured or malformed — gracefully hide
  if (!widgetClass) {
    return null;
  }

  return (
    <section ref={sectionRef} className="py-16 md:py-24 bg-accent/30 overflow-hidden">
      <div className="container mx-auto px-4">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-3 mb-4 group"
            aria-label="Follow ShoreSight Marine on Instagram"
          >
            <Instagram className="h-8 w-8 text-[hsl(330,70%,50%)] group-hover:scale-110 transition-transform" />
            <h2 className="text-3xl md:text-4xl font-bold text-foreground group-hover:text-azure-500 transition-colors">
              Follow Us on Instagram
            </h2>
          </a>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            See our latest projects and behind-the-scenes work
          </p>
        </motion.div>

        {/* Elfsight widget */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-6xl mx-auto"
        >
          <div
            ref={widgetRef}
            className={widgetClass}
            data-elfsight-app-lazy
          />
        </motion.div>

        {/* CTA button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="flex justify-center mt-10"
        >
          <Button
            size="lg"
            asChild
            className="bg-azure-500 hover:bg-azure-hover text-white"
          >
            <a
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2"
            >
              <Instagram className="h-5 w-5" />
              Follow @shoresightmarine on Instagram
              <ExternalLink className="h-4 w-4" />
            </a>
          </Button>
        </motion.div>
      </div>
    </section>
  );
};
